import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getAgents } from '../../services/agentService';
import { getPersonas } from '../../services/personaService';
import { getKnowledgeBases } from '../../services/knowledgeBaseService';
import { getPrompts } from '../../services/promptService';
import { faEnvelope, faHourglass, faObjectUngroup, faUser } from '@fortawesome/free-regular-svg-icons';
import { faCalendarTimes, faChartDiagram, faCircleStop, faCode, faDiagramPredecessor, faLandmark, faPaperPlane, faPlay, faPlayCircle, faPlug, faProjectDiagram, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getModels } from '../../services/modelHubService';
import PythonCodeEditor from './PythonCodeEditor';
import { Button } from '../../styles/PageStyles';
import Modal from '../modals/Modal';
import { MultiSelectDropdownSearch } from '../common/MultiSelectSearch';
import { fetchMCPServer } from '../../services/MCPService';
import { evaluateNodeExpression, getNodeConfig, getNodesForPipeline, getWorkStreams } from '../../services/pipelineService';
import { fetchCountByFilter, fetchEntityByPagination, getAllEntitiesByFilter } from '../../services/entityService';
import { FaTrash } from 'react-icons/fa';
import { getVisibleFields } from '../../utils/commonUtils';
import { DynamicField } from '../common/DynamicField';
import { fetchUsers, fetchUsersGropus } from '../../services/authService';
import { mandatoryFields } from '../../constants/nodeDefaults';
import { getTemplateCodes } from '../../services/emailService';

const PanelContainer = styled.div`
  width: 100%;
  background: white;
  border-left: 1px solid #e0e0e0;
  padding: 1rem;
  overflow-y: auto;
`;

const PanelTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const NoSelection = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  text-align: center;
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 0.5rem;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  margin-bottom: 0.1rem;
  color: var(--text-secondary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const NodeTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.background || '#f0f0f0'};
  color: ${props => props.color || 'var(--text-primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1rem;
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const NodeInfo = styled.div`
  flex: 1;
`;


const NodeName = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const SectionTitle = styled.h4`
  font-size: 0.9rem;
  margin: 1.5rem 0 0.8rem;
  color: var(--text-secondary);
  padding-bottom: 0.3rem;
  border-bottom: 1px solid #f5f5f5;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Tag = styled.div`
  background: #f0f0f0;
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const AddTagButton = styled.button`
  background: transparent;
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const RemoveTag = styled.span`
  cursor: pointer;
  font-size: 0.7rem;
  
  &:hover {
    color: #ff3b30;
  }
`;

const PipelineSection = styled.div`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const TrashSection = styled.span`
  padding: 0 12px;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    color: #9ba6b5;
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: #7f8ca0;
  }
`;
let cachedFileNodeConfig = null;
let cachedTimerNodeConfig = null;
let cachedTaskNodeConfig = null;

const FileNodeConfig = ({ selectedNode, handleInputChange }) => {
  const [formValues, setFormValues] = useState(selectedNode.data.data || {});
  const [configData, setConfigData] = useState([]);

  useEffect(() => {
    const fetchFileNodeConfig = async () => {
      if (cachedFileNodeConfig) {
        setConfigData(cachedFileNodeConfig);
        return;
      }

      const resp = await getNodeConfig('file_input');
      const attrs = resp['attributes'] || [];
      cachedFileNodeConfig = attrs; 
      setConfigData(attrs);
    };

    fetchFileNodeConfig();
  }, []);


  const handleChange = (name, value) => {
    setFormValues((prev) => {
      let updated = { ...prev, [name]: value };

      const changedField = configData.find(f => f.name === name);

      if (changedField) {
        const childFields = configData.filter(f => f.parent_key === name);

        childFields.forEach((child) => {
          const isValid = child.parent_value?.includes(value);
          if (!isValid && updated.hasOwnProperty(child.name)) {
            delete updated[child.name];
          }
        });
      }

      handleInputChange(`data.${name}`, value);
      return updated;
    });
  };

  const visibleFields = getVisibleFields(configData, formValues);

  return (
    <>
      {visibleFields.map(field => (
        <DynamicField
          key={field.name}
          field={field}
          value={formValues[field.name]}
          onChange={handleChange}
        />
      ))}
    </>
  );
};

const TaskNode = ({ selectedNode, handleInputChange, selectedPipeline = {} }) => {
  const [entityData, setEntityData] = useState([]);
  const [entityDocumentData, setEntityDocumentData] = useState([]);
  const [entityCount, setEntityCount] = useState(0);
  const [entityDocumentCount, setEntityDocumentCount] = useState(0);
  const [userList, setUserList] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [fieldNodeConfig, setFieldNodeConfig] = useState([]);
  const [formValues, setFormValues] = useState(selectedNode.data.data || {});



  const mandetoryFildsOfTask = mandatoryFields['taskServiceNode'];
  const customFieldValues = {
    entity : entityData,
    associated_entities : entityData,
    docs : entityDocumentData,
    assigned_user: userList,
    assigned_group : userGroups
  }

  useEffect(() => {
    const fetchEntityCount = async () => {
      const resp = await fetchCountByFilter("entity", {type:'entity'});
      if (resp) {
        console.log("entitycount", resp)
        setEntityCount(resp)
      }
    }

    const fetchEntityDocumentCount = async () => {
      const resp = await fetchCountByFilter("entity", {type:'document'});
      if(resp) {
        console.log("doccount", resp)
        setEntityDocumentCount(resp);
      }
    }

    const getUsers = async () => {
      const resp = await fetchUsers();
      if(resp) {
        const updatedUser = resp.map((user) => (
          { id:user.id ,
            name: `${user.first_name} ${user.last_name}`,
            userName: user.username,
            lastLoginUser: user.last_logged_in_company_cd
          }
        ))
        setUserList(updatedUser);
      }
    }

    const getUserGroups = async () => {
      const resp = await fetchUsersGropus();
      if(resp) {
        setUserGroups(resp);
      }
    }

    const fetchMetadata = async () => {
      if(cachedTaskNodeConfig) {
        setFieldNodeConfig(cachedTaskNodeConfig)
      }
      const resp = await getNodeConfig('task_service')
      const attrs = resp['attributes'] || [];
      setFieldNodeConfig(attrs)
      cachedTaskNodeConfig = attrs;

    }

    getUsers();
    getUserGroups();
    fetchEntityCount();
    fetchEntityDocumentCount();
    fetchMetadata();
  }, [])


  useEffect(() => {
    if(selectedNode.type == "taskServiceNode") {
    setDefaultEntitysAndAssosiatedEntities();
    }
  }, [selectedNode.id])


  useEffect(() => {
    const fetchEntites = async () => {
      const resp = await fetchEntityByPagination(0, entityCount, { type: 'entity' });
      const filteredDataModelEntities = resp?.map((entity) => {
        return {
          id: entity.id,
          name: entity.name,
          description: entity.description || entity.desc
        }
      })
      setEntityData(filteredDataModelEntities)
    }
    if (entityCount !== 0) fetchEntites()

  }, [entityCount])

   useEffect(() => {
    const fetchDocEntites = async () => {
      const resp = await fetchEntityByPagination(0, entityDocumentCount, {type:'document'});
      setEntityDocumentData(resp)
      console.log("doc resp", resp)
    }
    if (entityDocumentCount !== 0) fetchDocEntites()

  }, [entityDocumentCount])

  const handleMultiselectDropdownChange = (path, data) => {
    handleInputChange(path, data);
  }

  const isMandetoryField = (name) => {
    if(mandetoryFildsOfTask.includes(name)) {
       return <i class="fa-solid fa-asterisk" style={{fontSize:'8px', marginLeft:'4px', color:'#d10000'}}></i>
    }
  }

  const setDefaultEntitysAndAssosiatedEntities = () => {
    const rootEntity = selectedPipeline?.entity_name || "";
    const defaultEntity = [{ id: Date.now(), name: rootEntity, description: "" }]
    !formValues['entity'] && rootEntity && handleMultiselectDropdownChange('data.entity', defaultEntity);

    const rooAssociatedEntites = selectedPipeline?.associated_entities?.map((ele) => {
      return {
        id: new Date(),
        name: ele.entity_name,
        description: ""
      }
    });

    !formValues['associated_entities'] && rooAssociatedEntites && handleMultiselectDropdownChange('data.associated_entities', rooAssociatedEntites)
  }

  
  const handleChange = (name, value) => {
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value }
      handleInputChange(`data.${name}`, value);
      return updated
    })
  }
  return (
    <>
      {fieldNodeConfig?.map((field) => (
        <DynamicField
          key={field.name}
          field={field}
          value={formValues[field.name]}
          onChange={handleChange}
          customFieldValues={customFieldValues}
        />
      ))}
    </>
  )
}

const EmailNode = ({selectedNode, handleInputChange, selectedPipeline = {} }) => {
  const [templateCodes, setTemplateCodes] = useState([]);

  useEffect(() => {
    const fetchTemplteCodes = async () => {
      const resp = await getTemplateCodes();
      const templtes = resp?.map((item) => ({id:item.id, label: item.name, value:item.code}))
      setTemplateCodes(templtes)
    }

    fetchTemplteCodes();

  },[])

  console.log("selectd node",selectedNode)
  console.log("selectd node",selectedNode)

   return (
      <>
        <FormGroup>
          <Label htmlFor="tool-from">From</Label>
          <Input
            id="tool-from"
            value={selectedNode.data.data.frm || ''}
            onChange={(e) => handleInputChange('data.frm', e.target.value)}
            placeholder="e.g. meta-agent@metamorphtech.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-to">To</Label>
          <Input
            id="tool-to"
            value={selectedNode.data.data.to || ''}
            onChange={(e) => handleInputChange('data.to', e.target.value)}
            placeholder="e.g. recipient@example.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-cc">CC</Label>
          <Input
            id="tool-cc"
            value={selectedNode.data.data.cc || ''}
            onChange={(e) => handleInputChange('data.cc', e.target.value)}
            placeholder="e.g. cc@example.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-subject">Subject</Label>
          <Input
            id="tool-subject"
            value={selectedNode.data.data.subject || ''}
            onChange={(e) => handleInputChange('data.subject', e.target.value)}
            placeholder=""
          />
        </FormGroup>

                 <FormGroup >
                    <Label htmlFor="template_cd">Template Code 
                                                  </Label>
                    <Select id="template_cd" 
                                 value={selectedNode.data.data.template_cd || ''}
                                 onChange={(e) => handleInputChange('data.template_cd', e.target.value)}
                    >
                      <option value="">Select template</option>
                      {templateCodes?.map((opt, index) => (
                        <option key={opt.id || index} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>   
                  </FormGroup>



      </>
    );
}


const TimerNode = ({ selectedNode, handleInputChange }) => {
  const { data } = selectedNode.data;
  const [formValues, setFormValues] = useState(selectedNode.data.data || {});
  const [fieldConfig, setFieldConfig] = useState([]);

  useEffect(() => {
    const getMetadata = async () => {
      if (cachedTimerNodeConfig) {
        setFieldConfig(cachedTimerNodeConfig);
        return;
      }

      const resp = await getNodeConfig('timer_node')
      const attrs = resp['attributes'] || [];
      cachedTimerNodeConfig = attrs;
      setFieldConfig(attrs);
    }

    getMetadata();
  }, [])

  const handleChange = (name, value) => {
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value }
      handleInputChange(`data.${name}`, value);
      return updated
    })
  }

  return (
    <>
      {
        fieldConfig.map((field) => {
          return <DynamicField
            key={field.name}
            field={field}
            value={formValues[field.name]}
            onChange={handleChange}
          />
        })
      }

    </>
  )
}


const PropertiesPanel = ({
  selectedNode,
  updateNodeProperties,
  pipelineDescription,
  setPipelineDescription,
  selectedPipeline,
  setSelectedPipeline,
  selectedEdge,
  updateEdgeProperties,
}) => {

  const [agentList, setAgentList] = useState();
  const [promptList, setPromptList] = useState();
  const [modelList, setModelList] = useState();
  const [personaList, setPersonaList] = useState();
  const [knowledgeBaseList, setKnowledgeBaseList] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [workStreamList, setWorkStreamList] = useState([]);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [workflowVarsMode, setWorkflowVarsMode] = useState('manual'); // 'manual' or 'lastRun'
  const [workflowVars, setWorkflowVars] = useState({});
  const [evalResult, setEvalResult] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [lastRunVars, setLastRunVars] = useState(null);
  const [localCodes, setLocalCodes] = useState([]);
  const [localMCPServerData, setLocalMCPServerData] = useState({});
  const [isVisisbleMCPTools, setIsVisibleMCPTools] = useState(false)
  const [mcpToolsData, setMcpToolsData] = useState(null);
  const [selectedMCPServer, setSelectedMCPServer] = useState(null);
  const [headerDataConfig, setHeaderDataConfig] = useState([{name:"", value:""}])
  const [payloadData, setPayloadData] = useState(JSON.stringify(selectedNode?.data?.data?.payload, null, 2))
  useEffect(() => {
    if (selectedEdge) {
      setLocalCodes(selectedEdge.data?.conditions || []);
    }
  }, [selectedEdge]);


  useEffect(() => {
    if (!selectedNode?.id) return;
    if (
      localMCPServerData.mcp_servers &&
      selectedNode.data?.mcp_servers !== localMCPServerData.mcp_servers
    ) {
      updateNodeProperties(selectedNode.id, { mcp_servers: localMCPServerData.mcp_servers });
    }
  }, [localMCPServerData, updateNodeProperties]);

  useEffect(() => {
    if (selectedNode?.id) {
      if (selectedNode.data?.mcp_servers) {
        setLocalMCPServerData(selectedNode.data.mcp_servers);
      } else {
        setLocalMCPServerData({});
      }
      const headers = selectedNode.data?.data?.headers;
      if (headers && Object.keys(headers).length > 0) {
        setHeaderDataConfig(convertHeadersToConfig(selectedNode.data?.data.headers));
      }
      if (selectedNode?.data?.data?.payload) {
        setPayloadData(JSON.stringify(selectedNode.data.data.payload, null, 2));
      }
    }
  }, [selectedNode?.id]);

  useEffect(() => {
    const fetchData = async () => {
      let query = `skip=0&limit=200`;
      try {
        // Call all APIs concurrently
        const [agents, personas, knowledgeBases, prompt, models, workStreams] = await Promise.all([
          getAgents({ status: "ACTIVE" }, {}, query),
          getPersonas(null, null, query),
          getKnowledgeBases(null, null, query),
          getPrompts(null, null, query),
          getModels(null, null, query),
          getWorkStreams(null, null, query)
        ]);

        // Fetch entities separately
        const entitiesResponse = await getAllEntitiesByFilter({ type: "entity" }, {}, query);

        // Update state with the fetched data
        setAgentList(agents || []);
        setPersonaList(personas || []);
        setKnowledgeBaseList(knowledgeBases || []);
        setPromptList(prompt || []);
        setModelList(models || []);
        setEntityList(entitiesResponse || []);
        console.log("Work streams fetched:", workStreams);
        setWorkStreamList(workStreams || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const fetchMCPServers = async () => {
      try {
        const data = await fetchMCPServer();
        setMcpToolsData(data);
        const MCPServerList = data?.server_indexed_mcp_tools && Object.keys(data?.server_indexed_mcp_tools);
        setSelectedMCPServer(MCPServerList[0]);

      } catch (error) {
        console.log("Something went wrong while fetching the mcp tools.")
      }

    }

    fetchData();
    fetchMCPServers();
    if (selectedNode?.data?.agent_code) { setMCPToolsBasisOnAgent(selectedNode?.data?.agent_code) }
  }, []);

  function convertHeadersToConfig(headersObj) {
    if (!headersObj || typeof headersObj !== "object") return [{ name: "", value: "" }];
    const config = Object.entries(headersObj).map(([key, value]) => ({
      name: key,
      value: value
    }));
    return config;
  };


  if (selectedEdge) {

    const conditions = selectedEdge.data?.conditions || [];


    const handleConditionChange = (idx, value) => {
      const newLocalCodes = [...localCodes];
      newLocalCodes[idx].condition = value;
      setLocalCodes(newLocalCodes);
      // Also update the edge properties in parent
      const newConditions = [...localCodes];
      newConditions[idx].condition = value;
      updateEdgeProperties(selectedEdge.id, { conditions: newConditions });
    };

    const handleLanguageChange = (idx, newLang) => {
      setLocalCodes(prev => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], lang: newLang };
        return updated;
      });
    };

    const handleAddCondition = () => {
      const currentConditions = Array.isArray(conditions) ? conditions : [];
      const newConditions = [...currentConditions, { lang: '', condition: '' }];
      updateEdgeProperties(selectedEdge.id, { conditions: newConditions });
    };

    const handleRemoveCondition = (idx) => {
      const newConditions = conditions.filter((_, i) => i !== idx);
      updateEdgeProperties(selectedEdge.id, { conditions: newConditions });
    };



    return (
      <PanelContainer>
        <PanelTitle>Edge Properties</PanelTitle>
        <SectionTitle>Conditions</SectionTitle>
        {localCodes.map((localCode, idx) => (
          <FormGroup key={idx}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Label>Condition {idx + 1}</Label>
              <Select value={localCode.lang}
                onChange={(e) => handleLanguageChange(idx, e.target.value)} style={{ margin: '6px 0' }}>
                <option value="">Select language</option>
                <option value="python">Python</option>
                <option value="eng">English</option>

              </Select>
            </div>

            <PythonCodeEditor
              value={localCode.condition}
              lang={localCode.lang}
              onChange={val => handleConditionChange(idx, val)}
              placeholder={
                localCode?.lang == 'eng'
                  ? 'Enter contextual code'
                  : 'Enter condition code'
              }
            />
            <button
              style={{
                marginTop: '0.3rem',
                background: '#ffe0e0',
                color: '#b00020',
                border: 'none',
                borderRadius: '4px',
                padding: '0.2rem 0.7rem',
                cursor: 'pointer'
              }}
              onClick={() => handleRemoveCondition(idx)}
            >
              Remove
            </button>
          </FormGroup>
        ))}
        <button
          style={{
            background: '#e0f7fa',
            color: '#00796b',
            border: 'none',
            borderRadius: '4px',
            padding: '0.4rem 1rem',
            cursor: 'pointer'
          }}
          onClick={handleAddCondition}
          type="button"
        >
          + Add Condition
        </button>
      </PanelContainer>
    );
  }

  if (!selectedNode) {
    return (
      <PanelContainer>
        <PanelTitle>Properties</PanelTitle>

        <PipelineSection>
          <SectionTitle>Pipeline Properties</SectionTitle>

          <FormGroup>
            <Label htmlFor="pipeline-description">Description</Label>
            <Textarea
              id="pipeline-description"
              value={selectedPipeline?.description || ''}
              onChange={(e) => {
                // Ensure we're creating a new object with all existing properties
                const updatedPipeline = {
                  ...selectedPipeline,
                  description: e.target.value,
                  // Ensure other fields exist with default values if they don't already
                  value_stream: selectedPipeline?.value_stream || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };

                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }

                setSelectedPipeline(updatedPipeline);
              }}
              placeholder="Describe the purpose of this pipeline"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="value-stream">Value Stream</Label>
            <Select
              id="value-stream"
              value={selectedPipeline?.value_stream || ''}
              onChange={(e) => {
                const updatedPipeline = {
                  ...selectedPipeline,
                  value_stream: e.target.value,
                  // Ensure other fields exist
                  description: selectedPipeline?.description || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };

                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }

                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="">Select Work Stream</option>
              {workStreamList && workStreamList.length > 0 ? (
                workStreamList.map((stream, index) => (
                  <option key={index} value={stream.name}>{stream.name}</option>
                ))
              ) : (
                <>
                  <option value="New Business">New Business</option>
                  <option value="Claims Processing">Claims Processing</option>
                  <option value="Servicing">Servicing</option>
                  <option value="Fraud waste and abuse">Fraud waste and abuse</option>
                </>
              )}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="entity-name">Entity Name</Label>
            <Select
              id="entity-name"
              value={selectedPipeline?.entity_name || ''}
              onChange={(e) => {
                const updatedPipeline = {
                  ...selectedPipeline,
                  entity_name: e.target.value,
                  // Ensure other fields exist
                  description: selectedPipeline?.description || '',
                  value_stream: selectedPipeline?.value_stream || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };

                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }

                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="">Select Entity</option>
              {entityList.map((entity, index) => (
                <option key={index} value={entity.name}>{entity.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Associated Entities</Label>
            <div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                <Select
                  id="entity-select"
                  style={{ flex: 1 }}
                >
                  <option value="">Select Entity to Add</option>
                  {entityList.filter(entity => {
                    // Filter out entities that are already in associated_entities
                    const associated = selectedPipeline?.associated_entities || [];
                    return !associated.some(item =>
                      (typeof item === 'object' && item?.entity_name === entity.name)
                    );
                  }).map((entity, index) => (
                    <option key={index} value={entity.name}>{entity.name}</option>
                  ))}
                </Select>
                <button
                  type="button"
                  style={{
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}
                  onClick={() => {
                    const selectElement = document.getElementById('entity-select');
                    if (!selectElement || !selectElement.value) return;

                    const entityName = selectElement.value;
                    const currentEntities = selectedPipeline?.associated_entities || [];

                    // Create entity object with entity_name property
                    const entityToAdd = { entity_name: entityName };

                    // Prevent duplicate entities by checking entity_name
                    if (currentEntities.some(item =>
                      (typeof item === 'object' && item?.entity_name === entityName)
                    )) return;

                    const updatedPipeline = {
                      ...selectedPipeline,
                      associated_entities: [...currentEntities, entityToAdd],
                      // Ensure other fields exist
                      description: selectedPipeline?.description || '',
                      value_stream: selectedPipeline?.value_stream || '',
                      entity_name: selectedPipeline?.entity_name || '',
                      knowledgebase: selectedPipeline?.knowledgebase || ''
                    };
                    setSelectedPipeline(updatedPipeline);

                    // Reset the select to default option after adding
                    selectElement.value = '';
                  }}
                >
                  + Add Entity
                </button>
              </div>



              <TagsContainer>
                {(selectedPipeline?.associated_entities || []).map((entity, index) => {
                  // Get entity name from the entity_name property
                  const entityName = entity.entity_name || 'Unknown Entity';

                  return (
                    <Tag key={index}>
                      {entityName}
                      <RemoveTag onClick={() => {
                        const newEntities = [...(selectedPipeline?.associated_entities || [])];
                        newEntities.splice(index, 1);
                        const updatedPipeline = {
                          ...selectedPipeline,
                          associated_entities: newEntities,
                          // Ensure other fields exist
                          description: selectedPipeline?.description || '',
                          value_stream: selectedPipeline?.value_stream || '',
                          entity_name: selectedPipeline?.entity_name || '',
                          knowledgebase: selectedPipeline?.knowledgebase || ''
                        };
                        setSelectedPipeline(updatedPipeline);
                      }}>✕</RemoveTag>
                    </Tag>
                  );
                })}
                {(selectedPipeline?.associated_entities || []).length === 0 && (
                  <div style={{ color: '#888', fontSize: '0.9rem' }}>No associated entities selected</div>
                )}
              </TagsContainer>
            </div>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="knowledgebase">Knowledge Base</Label>
            <Select
              id="knowledgebase"
              value={selectedPipeline?.knowledgebase || ''}
              onChange={(e) => {
                const updatedPipeline = {
                  ...selectedPipeline,
                  knowledgebase: e.target.value,
                  // Ensure other fields exist
                  description: selectedPipeline?.description || '',
                  value_stream: selectedPipeline?.value_stream || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || []
                };

                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }

                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="">Select Knowledge Base</option>
              {knowledgeBaseList.map((kb, index) => (
                <option key={index} value={kb.code}>{kb.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor='prefix'>Prefix</Label>
            <Input placeholder='Enter prefix'
             id="prefix"
             onChange={(e) => {
                const updatedPipeline = {
                  ...selectedPipeline,
                  prefix: e.target.value
                };
                setSelectedPipeline(updatedPipeline)
              
            }} />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="pipeline-type">Type</Label>
            <Select
              id="pipeline-type"
              value={selectedPipeline?.type || 'pipeline'}
              onChange={(e) => {
                const updatedPipeline = {
                  ...selectedPipeline,
                  type: e.target.value,
                  // Ensure other fields exist with default values if they don't already
                  description: selectedPipeline?.description || '',
                  value_stream: selectedPipeline?.value_stream || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };

                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }

                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="pipeline">Pipeline</option>
              <option value="tool">Tool</option>
            </Select>
          </FormGroup>
          {/* Add CRON Scheduler fields */}
          <FormGroup>
            <Label htmlFor="schedule-enabled">
              Schedule Run
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                marginLeft: '10px',
                gap: '8px'
              }}>
                <input
                  type="checkbox"
                  id="schedule-enabled"
                  checked={selectedPipeline?.schedule_enabled || false}
                  onChange={(e) => {
                    const updatedPipeline = {
                      ...selectedPipeline,
                      schedule_enabled: e.target.checked,
                      // Reset cron fields when disabled
                      cron_config: e.target.checked ? (selectedPipeline?.cron_config || {
                        cron_minute: '0',
                        cron_hour: '0',
                        cron_day: '*',
                        cron_month: '*',
                        cron_weekday: '0',
                        cron_command: '0 0 * * 0'
                      }) : {
                        cron_minute: '0',
                        cron_hour: '0',
                        cron_day: '*',
                        cron_month: '*',
                        cron_weekday: '0',
                        cron_command: '0 0 * * 0'
                      }
                    };
                    setSelectedPipeline(updatedPipeline);
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  {selectedPipeline?.schedule_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </Label>
          </FormGroup>

          {selectedPipeline?.schedule_enabled && (
            <>
              <div style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                padding: '1rem',
                marginTop: '0.5rem'
              }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: '500', 
                  marginBottom: '1rem',
                  color: 'var(--text-primary)'
                }}>
                  Common Settings
                </div>
                
                <FormGroup>
                  <Label htmlFor="cron-preset">Schedule Preset</Label>
                  <Select
                    id="cron-preset"
                    onChange={(e) => {
                      const presets = {
                        'daily': { minute: '0', hour: '0', day: '*', month: '*', weekday: '*' },
                        'weekly': { minute: '0', hour: '0', day: '*', month: '*', weekday: '0' },
                        'monthly': { minute: '0', hour: '0', day: '1', month: '*', weekday: '*' },
                        'hourly': { minute: '0', hour: '*', day: '*', month: '*', weekday: '*' }
                      };
                      
                      if (e.target.value && presets[e.target.value]) {
                        const preset = presets[e.target.value];
                        const cronCommand = `${preset.minute} ${preset.hour} ${preset.day} ${preset.month} ${preset.weekday}`;
                        const updatedPipeline = {
                          ...selectedPipeline,
                          cron_config: {
                            ...selectedPipeline?.cron_config,
                            cron_minute: preset.minute,
                            cron_hour: preset.hour,
                            cron_day: preset.day,
                            cron_month: preset.month,
                            cron_weekday: preset.weekday,
                            cron_command: cronCommand
                          }
                        };
                        setSelectedPipeline(updatedPipeline);
                      }
                    }}
                  >
                    <option value="">Select Preset</option>
                    <option value="daily">Daily (At midnight)</option>
                    <option value="weekly">Weekly (Sunday at midnight)</option>
                    <option value="monthly">Monthly (1st at midnight)</option>
                    <option value="hourly">Hourly (Every hour)</option>
                  </Select>
                </FormGroup>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <FormGroup>
                    <Label htmlFor="cron-minute">Minute</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Input
                        id="cron-minute"
                        type="text"
                        value={selectedPipeline?.cron_config?.cron_minute || '0'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_minute: e.target.value,
                            cron_command: `${e.target.value} ${cronConfig.cron_hour || '0'} ${cronConfig.cron_day || '*'} ${cronConfig.cron_month || '*'} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                        style={{ flex: 1 }}
                        placeholder="0-59 or *"
                      />
                      <Select
                        style={{ flex: 1 }}
                        value={selectedPipeline?.cron_config?.cron_minute || '0'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_minute: e.target.value,
                            cron_command: `${e.target.value} ${cronConfig.cron_hour || '0'} ${cronConfig.cron_day || '*'} ${cronConfig.cron_month || '*'} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                      >
                        <option value="0">:00 (At the beginning of the hour) (0)</option>
                        <option value="15">:15 (Quarter past) (15)</option>
                        <option value="30">:30 (Half past) (30)</option>
                        <option value="45">:45 (Quarter to) (45)</option>
                        <option value="*">Every minute (*)</option>
                      </Select>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="cron-hour">Hour</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Input
                        id="cron-hour"
                        type="text"
                        value={selectedPipeline?.cron_config?.cron_hour || '0'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_hour: e.target.value,
                            cron_command: `${cronConfig.cron_minute || '0'} ${e.target.value} ${cronConfig.cron_day || '*'} ${cronConfig.cron_month || '*'} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                        style={{ flex: 1 }}
                        placeholder="0-23 or *"
                      />
                      <Select
                        style={{ flex: 1 }}
                        value={selectedPipeline?.cron_config?.cron_hour || '0'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_hour: e.target.value,
                            cron_command: `${cronConfig.cron_minute || '0'} ${e.target.value} ${cronConfig.cron_day || '*'} ${cronConfig.cron_month || '*'} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                      >
                        <option value="0">12:00 a.m. Midnight (0)</option>
                        <option value="6">6:00 a.m. (6)</option>
                        <option value="12">12:00 p.m. Noon (12)</option>
                        <option value="18">6:00 p.m. (18)</option>
                        <option value="*">Every hour (*)</option>
                      </Select>
                    </div>
                  </FormGroup>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <FormGroup>
                    <Label htmlFor="cron-day">Day</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Input
                        id="cron-day"
                        type="text"
                        value={selectedPipeline?.cron_config?.cron_day || '*'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_day: e.target.value,
                            cron_command: `${cronConfig.cron_minute || '0'} ${cronConfig.cron_hour || '0'} ${e.target.value} ${cronConfig.cron_month || '*'} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                        style={{ flex: 1 }}
                        placeholder="1-31 or *"
                      />
                      <Select
                        style={{ flex: 1 }}
                        value={selectedPipeline?.cron_config?.cron_day || '*'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_day: e.target.value,
                            cron_command: `${cronConfig.cron_minute || '0'} ${cronConfig.cron_hour || '0'} ${e.target.value} ${cronConfig.cron_month || '*'} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                      >
                        <option value="*">Every Day (*)</option>
                        <option value="1">1st of month</option>
                        <option value="15">15th of month</option>
                        <option value="L">Last day of month</option>
                      </Select>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="cron-month">Month</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Input
                        id="cron-month"
                        type="text"
                        value={selectedPipeline?.cron_config?.cron_month || '*'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_month: e.target.value,
                            cron_command: `${cronConfig.cron_minute || '0'} ${cronConfig.cron_hour || '0'} ${cronConfig.cron_day || '*'} ${e.target.value} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                        style={{ flex: 1 }}
                        placeholder="1-12 or *"
                      />
                      <Select
                        style={{ flex: 1 }}
                        value={selectedPipeline?.cron_config?.cron_month || '*'}
                        onChange={(e) => {
                          const cronConfig = selectedPipeline?.cron_config || {};
                          const newCronConfig = {
                            ...cronConfig,
                            cron_month: e.target.value,
                            cron_command: `${cronConfig.cron_minute || '0'} ${cronConfig.cron_hour || '0'} ${cronConfig.cron_day || '*'} ${e.target.value} ${cronConfig.cron_weekday || '0'}`
                          };
                          const updatedPipeline = {
                            ...selectedPipeline,
                            cron_config: newCronConfig
                          };
                          setSelectedPipeline(updatedPipeline);
                        }}
                      >
                        <option value="*">Every Month (*)</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </Select>
                    </div>
                  </FormGroup>
                </div>

                <FormGroup>
                  <Label htmlFor="cron-weekday">Weekday</Label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Input
                      id="cron-weekday"
                      type="text"
                      value={selectedPipeline?.cron_config?.cron_weekday || '0'}
                      onChange={(e) => {
                        const cronConfig = selectedPipeline?.cron_config || {};
                        const newCronConfig = {
                          ...cronConfig,
                          cron_weekday: e.target.value,
                          cron_command: `${cronConfig.cron_minute || '0'} ${cronConfig.cron_hour || '0'} ${cronConfig.cron_day || '*'} ${cronConfig.cron_month || '*'} ${e.target.value}`
                        };
                        const updatedPipeline = {
                          ...selectedPipeline,
                          cron_config: newCronConfig
                        };
                        setSelectedPipeline(updatedPipeline);
                      }}
                      style={{ flex: 1 }}
                      placeholder="0-7 or *"
                    />
                    <Select
                      style={{ flex: 1 }}
                      value={selectedPipeline?.cron_config?.cron_weekday || '0'}
                      onChange={(e) => {
                        const cronConfig = selectedPipeline?.cron_config || {};
                        const newCronConfig = {
                          ...cronConfig,
                          cron_weekday: e.target.value,
                          cron_command: `${cronConfig.cron_minute || '0'} ${cronConfig.cron_hour || '0'} ${cronConfig.cron_day || '*'} ${cronConfig.cron_month || '*'} ${e.target.value}`
                        };
                        const updatedPipeline = {
                          ...selectedPipeline,
                          cron_config: newCronConfig
                        };
                        setSelectedPipeline(updatedPipeline);
                      }}
                    >
                      <option value="0">Sunday (0)</option>
                      <option value="1">Monday (1)</option>
                      <option value="2">Tuesday (2)</option>
                      <option value="3">Wednesday (3)</option>
                      <option value="4">Thursday (4)</option>
                      <option value="5">Friday (5)</option>
                      <option value="6">Saturday (6)</option>
                      <option value="*">Every Day (*)</option>
                    </Select>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="cron-command">Command (Generated CRON Pattern)</Label>
                  <Input
                    id="cron-command"
                    type="text"
                    value={selectedPipeline?.cron_config?.cron_command || `${selectedPipeline?.cron_config?.cron_minute || '0'} ${selectedPipeline?.cron_config?.cron_hour || '0'} ${selectedPipeline?.cron_config?.cron_day || '*'} ${selectedPipeline?.cron_config?.cron_month || '*'} ${selectedPipeline?.cron_config?.cron_weekday || '0'}`}
                    readOnly
                    style={{
                      background: '#f8f9fa',
                      fontFamily: 'monospace',
                      color: '#495057'
                    }}
                    title="This is the generated CRON expression based on your selections above"
                  />
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    marginTop: '0.25rem'
                  }}>
                    Format: minute hour day month weekday
                  </div>
                </FormGroup>
              </div>
            </>
          )}

        </PipelineSection>

        {/* <NoSelection>
          Select a node to edit its properties
        </NoSelection> */}
      </PanelContainer>
    );
  }

  const getNodeTypeInfo = (type) => {
    switch (type) {
      case 'triggerNode':
        return {
          label: 'Trigger',
          icon: faPlayCircle,
          background: '#FFF4CC',
          color: '#E6C200'
        };
      case 'outputNode':
        return {
          label: 'Output',
          icon: faCircleStop,
          background: '#FFE5CC',
          color: '#FF9900'
        };
      case 'agentNode':
        return {
          label: 'Agent',
          icon: faUser,
          background: '#CCF2FF',
          color: '#0099CC'
        };
      case 'dataSourceNode':
        return {
          label: 'Data Source',
          icon: faDiagramPredecessor,
          background: '#D9F9F2',
          color: '#26A69A'
        };
      case 'modelNode':
        return {
          label: 'Model',
          icon: faProjectDiagram,
          background: '#CCE5FF',
          color: '#337AB7'
        };
      case 'knowledgeNode':
        return {
          label: 'Knowledgebase',
          icon: faLandmark,
          background: '#E0F7FA',
          color: '#00ACC1'
        };
      case 'toolNode':
        return {
          label: 'Tool',
          icon: '⚒',
          background: '#fff5e6',
          color: '#ff9500'
        };

      case 'apiNode':
        return {
          label: 'API',
          icon: faPlug,
          background: '#E0F7E9',
          color: '#2E7D32'
        };
      case 'taskServiceNode' :
        return {
          label: 'Task',
          icon: faHourglass,
          background:'#EFE9E3',
          color:'#829591'
        }
      case 'emailOutNode':
        return {
          label: 'Email Out',
          icon: faEnvelope,
          background: '#E0F7E9',
          color: '#2E7D32'
        };

      case 'fileNode':
        return {
          label: 'File Operations',
          icon: faChartDiagram,
          background: '#F0F8FF',
          color: '#4169E1'
        };
      case 'timerNode':
        return {
          label: 'Timer',
          icon: faCalendarTimes,
          background: '#ECF4E8',
          color: '#63A361'
        };
      case 'classifierNode':
        return {
          label: 'Classifier',
          icon: faObjectUngroup,
          background: '#D6F5E3',
          color: '#388E3C'
        };

      case 'codeNode':
        return {
          label: 'Code Block',
          icon: faCode,
          background: '#E4FBE9',
          color: '#43A047'
        };

      case 'webHookNode':
        return {
          label: 'Tool',
          icon: faPaperPlane,
          background: '#D2F8D2',
          color: '#4CAF50'
        };
      default:
        return {
          label: 'Node',
          icon: '◆',
          background: '#f0f0f0',
          color: 'var(--text-primary)'
        };
    }
  };



  const nodeTypeInfo = getNodeTypeInfo(selectedNode.type);

  // const handleInputChange = (field, value) => {
  //   updateNodeProperties(selectedNode.id, { [field]: value });
  // };

  const handleInputChange = (field, value) => {
    if (field.startsWith('data.')) {
      const nestedField = field.split('.')[1];
      let tmpData = selectedNode.data.data
      tmpData[nestedField] = value;
      updateNodeProperties(selectedNode.id, { ['data']: tmpData });

    } else {
      // Handle top-level fields
      updateNodeProperties(selectedNode.id, { [field]: value });
    }
    if (field == 'agent_code') {
      setMCPToolsBasisOnAgent(value);
    }
  };


  const setMCPToolsBasisOnAgent = (agentCd) => {
    const selectedAgent = agentList.find(agent => agent.code == agentCd);
    selectedAgent?.mcp_servers && setLocalMCPServerData((prev) => {
      if (!selectedAgent?.mcp_servers) return { mcp_servers: {} }
      const data = { mcp_servers: { ...prev.mcp_servers, ...selectedAgent?.mcp_servers } }
      return data;
    })
  }

  const renderAgentProperties = () => {
    const { agent_code, persona, capabilities = [] } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="agent-type">Agent Type</Label>
          <Select
            id="agent-type"
            value={selectedNode.data.agent_code}
            onChange={(e) => handleInputChange('agent_code', e.target.value)} >
            <option value="">Select Agent</option>
            {agentList?.length && agentList.map((agent, index) => (
              <option key={index} value={agent.code}>{agent.name}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>MCP Server</Label>
          {!isVisisbleMCPTools && (!selectedNode?.data?.mcp_servers) && <TagsContainer>
            <AddTagButton onClick={() => { setIsVisibleMCPTools(true) }}>+ Add</AddTagButton>
          </TagsContainer>}
        </FormGroup>

        {
          (isVisisbleMCPTools ||
            (selectedNode?.data?.mcp_servers &&
              Object.keys(selectedNode?.data?.mcp_servers).length)) &&
          (() => {
            const selectedForThisServer =
              selectedNode?.data?.mcp_servers?.[selectedMCPServer] || [];
            const serverList = mcpToolsData?.server_indexed_mcp_tools && Object.keys(
              mcpToolsData.server_indexed_mcp_tools
            ) || [];


            const tools =
              mcpToolsData?.server_indexed_mcp_tools[selectedMCPServer];

            return (
              <>
                <Select
                  value={selectedMCPServer}
                  onChange={(e) => setSelectedMCPServer(e.target.value)}
                >
                  {serverList.map((server, index) => (
                    <option key={index}>{server}</option>
                  ))}
                </Select>

                <Label style={{ marginTop: '5px' }}>MCP Server Tools</Label>
                <MultiSelectDropdownSearch
                  options={tools}
                  selected={selectedForThisServer}
                  onChange={(newSelectedTools) => {
                    setLocalMCPServerData((prev) => {
                      const data = {
                        ...prev,
                        mcp_servers: {
                          ...(prev.mcp_servers || {}),
                          [selectedMCPServer]: newSelectedTools,
                        },
                      }
                      return data
                    });
                  }}
                  placeholder={`Select tools for ${selectedMCPServer}`}
                  displayKey="name"
                  valueKey="name"
                />
              </>
            );
          })()}







        <FormGroup>
          <Label htmlFor="agent-persona">Persona</Label>
          <Select
            id="agent-persona"
            value={persona || ''}
            onChange={(e) => handleInputChange('persona', e.target.value)}>
            <option value="">Select Persona</option>
            {personaList.map((persona, index) => (
              <option key={index} value={persona.code}>
                {persona.name}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Capabilities</Label>
          <TagsContainer>
            {capabilities.map((capability, index) => (
              <Tag key={index}>
                {capability}
                <RemoveTag onClick={() => {
                  const newCapabilities = [...capabilities];
                  newCapabilities.splice(index, 1);
                  handleInputChange('capabilities', newCapabilities);
                }}>✕</RemoveTag>
              </Tag>
            ))}
            <AddTagButton onClick={() => {
              const newCapability = prompt('Enter capability:');
              if (newCapability && !capabilities.includes(newCapability)) {
                handleInputChange('capabilities', [...capabilities, newCapability]);
              }
            }}>+ Add</AddTagButton>
          </TagsContainer>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="knowledge-type">Prompts</Label>
          <Select
            id="knowledge-check_list"
            value={selectedNode.data.check_list}
            onChange={(e) => handleInputChange('check_list', e.target.value)}
          >
            <option value="">Select</option>
            {promptList.map((prompt, index) => (
              <option key={index} value={prompt.code}>{prompt.name}</option>
            ))}
          </Select>
        </FormGroup>
      </>
    );
  };

  const renderModelProperties = () => {
    const { modelType, provider, version } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="model-type">Model Type</Label>
          <Select
            id="model-type"
            value={selectedNode.data.data.model_code}
            onChange={(e) => handleInputChange('data.model_code', e.target.value)} >
            <option value="">Select Model</option>
            {modelList.map((model, index) => (
              <option key={index} value={model.code}>{model.name}</option>
            ))}
          </Select>
        </FormGroup>

        {/* <FormGroup>
          <Label htmlFor="model-version">Version</Label>
          <Input
            id="model-version"
            value={version || ''}
            onChange={(e) => handleInputChange('version', e.target.value)}
            placeholder="e.g. gpt-4, claude-3, etc."
          />
        </FormGroup> */}
      </>
    );
  };

  const renderKnowledgeProperties = () => {
    const { knowledgeType, source } = selectedNode.data;
    return (
      <>
        <FormGroup>
          <Label htmlFor="knowledge-type">Knowledge Base</Label>
          <Select
            id="knowledge-type"
            value={selectedNode.data.data.kb_code}
            onChange={(e) => handleInputChange('data.kb_code', e.target.value)}
          >
            <option value="">Select</option>
            {knowledgeBaseList.map((kb, index) => (
              <option key={index} value={kb.code}>{kb.name}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="knowledge-type">Operation</Label>
          <Select
            id="knowledge-operation"
            value={selectedNode.data.data.operation}
            onChange={(e) => handleInputChange('data.operation', e.target.value)}
          >
            <option value="">Select</option>
            <option value='ingest'>Ingest</option>
            <option value='query'>Query</option>
            <option value='search'>Search</option>
            <option value='report'>Report</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="knowledge-type">Prompts</Label>
          <Select
            id="knowledge-check_list"
            value={selectedNode.data.check_list}
            onChange={(e) => handleInputChange('check_list', e.target.value)}
          >
            <option value="">Select</option>
            {promptList.map((prompt, index) => (
              <option key={index} value={prompt.code}>{prompt.name}</option>
            ))}
          </Select>
        </FormGroup>

        {/* <FormGroup>
          <Label htmlFor="knowledge-source">Source</Label>
          <Input
            id="knowledge-source"
            value={source || ''}
            onChange={(e) => handleInputChange('source', e.target.value)}
            placeholder="e.g. Claims KB, Policy Database, etc."
          />
        </FormGroup> */}
      </>
    );
  };

  const renderToolProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-type">Tool Type</Label>
          <Select
            id="tool-type"
            value={toolType}
            onChange={(e) => handleInputChange('toolType', e.target.value)}
          >
            <option value="api">API</option>
            <option value="code">Code Execution</option>
            <option value="webhook">Webhook</option>
            <option value="function">Function</option>
          </Select>
        </FormGroup>

        {toolType === 'api' || toolType === 'webhook' ? (
          <>
            <FormGroup>
              <Label htmlFor="tool-endpoint">Endpoint</Label>
              <Input
                id="tool-endpoint"
                value={endpoint || ''}
                onChange={(e) => handleInputChange('endpoint', e.target.value)}
                placeholder="e.g. https://api.example.com/v1/data"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tool-method">Method</Label>
              <Select
                id="tool-method"
                value={method || 'GET'}
                onChange={(e) => handleInputChange('method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </Select>
            </FormGroup>
          </>
        ) : toolType === 'code' ? (
          <FormGroup>
            <Label htmlFor="tool-code">
              Code
              <Button type="button" ><FontAwesomeIcon icon={faPlay} /></Button>
            </Label>
            {/* <Textarea
              id="tool-code"
              value={selectedNode.data.data.code_block || ''}
              onChange={(e) => handleInputChange('data.code_block', e.target.value)}
              placeholder="Enter code to execute"
              style={{ minHeight: '120px', fontFamily: 'monospace' }}
            /> */}
            <PythonCodeEditor
              value={selectedNode.data.data.code_block}
              onChange={val => handleInputChange('data.code_block', val)}
            />
          </FormGroup>
        ) : null}
      </>
    );
  };

  const renderApiProperties = () => {
    const { data: { headers, url, token ,tokenKey, method, payload={} } } = selectedNode.data;

    const handleHeaderChange = (type, value, index) => {
      const updatedHeaderConfig = headerDataConfig.map((config, i) => {
        if (i === index) {
          return { ...config, [type]: value };
        }
        return config;
      });
      setHeaderDataConfig(updatedHeaderConfig);
      createAndSetHeaderObject(updatedHeaderConfig);
    };

    const addNewHeader = () => {
      setHeaderDataConfig(prevData => [...prevData, { name: "", value: "" }]);
    }

    const createAndSetHeaderObject = (data) => {
      let headerObjectData = {};
      data.length && data.forEach(item => {
          headerObjectData[item.name] = item.value;
      });
      handleInputChange('data.headers', headerObjectData)
    }

    const handleRemoveHeader = (index) => {
      if (headerDataConfig.length > 1) {
        const updatedHeaderCofig = headerDataConfig.filter((_, idx) => idx !== index);
        setHeaderDataConfig(updatedHeaderCofig);
        createAndSetHeaderObject(updatedHeaderCofig)
      }
    }

    const handlePayloadChange = (e) => {
      const value = e.target.value;
      setPayloadData(value);

      try {
        const parsed = JSON.parse(value);
        handleInputChange("data.payload", parsed);
      } catch (error) {
        console.warn("Invalid JSON:", error.message);
      }
    };

    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-endpoint">Endpoint</Label>
          <Input
            id="tool-endpoint"
            value={url || ''}
            onChange={(e) => handleInputChange('data.url', e.target.value)}
            placeholder="e.g. https://api.example.com/v1/data"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="tool-header">Headers</Label>
          {
            headerDataConfig.length > 0 && headerDataConfig.map((config, index) => (
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', marginBottom: '8px' }}>
                <Input
                  id="tool-header"
                  value={config.name || ''}
                  onChange={(e) => handleHeaderChange("name", e.target.value, index)}
                  placeholder="name"
                />
                <Input
                  id="tool-header"
                  value={config.value || ''}
                  onChange={(e) => handleHeaderChange("value", e.target.value, index)}
                  placeholder="value"
                />
                <TrashSection onClick={() => handleRemoveHeader(index)}>
                  <FaTrash size={18} />
                </TrashSection>
              </div>
            ))
          }
          <AddTagButton onClick={() => addNewHeader()}>+ Add</AddTagButton>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="tool-token">Token</Label>
          <Input
            id="tool-token"
            value={token || ''}
            onChange={(e) => handleInputChange('data.token', e.target.value)}
            placeholder="e.g. Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJib..."
          />
        </FormGroup>


        {/* <FormGroup>
          <Label htmlFor="tool-tokenKey">Token Key</Label>
          <Input
            id="tool-tokenKey"
            value={tokenKey || ''}
            onChange={(e) => handleInputChange('data.tokenKey', e.target.value)}
            placeholder="e.g. api_key"
          />
        </FormGroup> */}

        <FormGroup>
          <Label htmlFor="tool-method">Method</Label>
          <Select
            id="tool-method"
            value={method}
            onChange={(e) => {
              handleInputChange('data.method', e.target.value)
              if (e.target.value == "GET") {
                handleInputChange('data.payload', "payloadStr")
              }
            }}
          >
            <option value="">Select</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </Select>
        </FormGroup>
        {(method !== 'GET') && <FormGroup>
          <Label htmlFor="tool-payload">Payload</Label>
          <Textarea
            id="tool-payload"
            value={payloadData || ''}
            onChange={(e) => handlePayloadChange(e)}
            placeholder=""
          />
        </FormGroup>}

      </>
    );
  };

  const renderEmailOutProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

   
  };

  const renderCodeProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-code">
            Code
            <Button type="button" onClick={handleOpenEvalModal}><FontAwesomeIcon icon={faPlay} /></Button>
          </Label>
          {/* <Textarea
            id="tool-code"
            value={selectedNode.data.data.code_block || ''}
            onChange={(e) => handleInputChange('data.code_block', e.target.value)}
            placeholder="Enter code to execute"
            style={{ minHeight: '120px', fontFamily: 'monospace' }}
          /> */}
          <PythonCodeEditor
            value={selectedNode.data.data.code_block}
            onChange={val => handleInputChange('data.code_block', val)}
          />
        </FormGroup>

      </>
    );
  };

  const renderClassifierProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="classes_knowledge_base">Classifier</Label>
          <Select
            id="classes_knowledge_base"
            value={selectedNode.data.data.classes_knowledge_base}
            onChange={(e) => handleInputChange('data.classes_knowledge_base', e.target.value)} >
            <option value="">Select Agent</option>
            {knowledgeBaseList.map((kb, index) => (
              <option key={index} value={kb.code}>{kb.name}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="classes_query">Class Query</Label>
          {/* <Input
            id="classes_queryt"
            value={selectedNode.data.data.classes_query || ''}
            onChange={(e) => handleInputChange('data.classes_query', e.target.value)}
            placeholder=""
          /> */}
          <Textarea
            id="classes_query"
            value={selectedNode.data.data.classes_query || ''}
            onChange={(e) => handleInputChange('data.classes_query', e.target.value)}
            style={{ minHeight: '120px' }}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="query_suffix">Query SUffix</Label>
          <Textarea
            id="query_suffix"
            value={selectedNode.data.data.query_suffix || ''}
            onChange={(e) => handleInputChange('data.query_suffix', e.target.value)}
            style={{ minHeight: '120px' }}
          />
        </FormGroup>
      </>
    );
  };

  const renderTriggerProperties = () => {
    const { triggerType, schedule } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="trigger-type">Trigger Type</Label>
          <Select
            id="trigger-type"
            value={triggerType}
            onChange={(e) => handleInputChange('triggerType', e.target.value)}
          >
            <option value="manual">Manual</option>
            <option value="scheduled">Scheduled</option>
            <option value="event">Event-based</option>
            <option value="webhook">Webhook</option>
          </Select>
        </FormGroup>

        {triggerType === 'scheduled' && (
          <FormGroup>
            <Label htmlFor="trigger-schedule">Schedule (cron)</Label>
            <Input
              id="trigger-schedule"
              value={schedule || ''}
              onChange={(e) => handleInputChange('schedule', e.target.value)}
              placeholder="e.g. 0 9 * * 1-5"
            />
          </FormGroup>
        )}
      </>
    );
  };

  const renderOutputProperties = () => {
    const { outputType, format, destination } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="output-type">Output Type</Label>
          <Select
            id="output-type"
            value={outputType}
            onChange={(e) => handleInputChange('outputType', e.target.value)}
          >
            <option value="response">Response</option>
            <option value="file">File</option>
            <option value="database">Database</option>
            <option value="webhook">Webhook</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="output-format">Format</Label>
          <Select
            id="output-format"
            value={format || 'json'}
            onChange={(e) => handleInputChange('format', e.target.value)}
          >
            <option value="json">JSON</option>
            <option value="xml">XML</option>
            <option value="csv">CSV</option>
            <option value="text">Plain Text</option>
          </Select>
        </FormGroup>

        {outputType !== 'response' && (
          <FormGroup>
            <Label htmlFor="output-destination">Destination</Label>
            <Input
              id="output-destination"
              value={destination || ''}
              onChange={(e) => handleInputChange('destination', e.target.value)}
              placeholder={outputType === 'file' ? 'e.g. /path/to/file.json' :
                outputType === 'database' ? 'e.g. claims_db.results' :
                  'e.g. https://webhook.example.com/callback'}
            />
          </FormGroup>
        )}
      </>
    );
  };

  const renderNodeSpecificProperties = () => {
    switch (selectedNode.type) {
      case 'agentNode':
        return renderAgentProperties();
      case 'modelNode':
        return renderModelProperties();
      case 'knowledgeNode':
        return renderKnowledgeProperties();
      case 'toolNode':
        return renderToolProperties();
      case 'triggerNode':
        return renderTriggerProperties();
      case 'emailOutNode':
        return <EmailNode
                 selectedNode={selectedNode}
                  handleInputChange={handleInputChange}
                  />;
      case 'fileNode':
        return <FileNodeConfig
                  selectedNode={selectedNode}
                  handleInputChange={handleInputChange}
                />;
      case 'apiNode':
        return renderApiProperties();
      case 'taskServiceNode': 
      return <TaskNode
                  selectedNode={selectedNode}
                  handleInputChange={handleInputChange} 
                  selectedPipeline={selectedPipeline}
                />;
      case 'timerNode' :
        return <TimerNode 
                selectedNode={selectedNode}
                handleInputChange={handleInputChange} 
                  />
      case 'codeNode':
        return renderCodeProperties();
      case 'classifierNode':
        return renderClassifierProperties();
      case 'outputNode':
        return renderOutputProperties();
      default:
        return null;
    }
  };

  // Helper to extract workflow_vars from code block (simple regex for demo)
  const extractWorkflowVars = (code) => {
    // Example: look for workflow_vars['varName'] or workflow_vars["varName"]
    const regex = /workflow_vars\[['"](\w+)['"]\]/g;
    const vars = {};
    let match;
    while ((match = regex.exec(code))) {
      vars[match[1]] = '';
    }
    return vars;
  };

  // Fetch last successful run workflow_vars
  const fetchLastRunVars = async () => {
    const nodeCode = selectedNode.id;
    if (!nodeCode) return;
    const filters = {
      transaction_status: 'CLOSED',
      code: nodeCode
    };
    const sort = { "created_at": "desc" };
    const results = await getNodesForPipeline(filters, sort);

    return results;

    // if (results && results.length > 0) {
    //   const node = results[0];
    //   setLastRunVars(node.workflow_vars || {});
    //   setWorkflowVars(node.workflow_vars || {});
    // }
  };

  // Open modal and initialize workflow vars
  const handleOpenEvalModal = async () => {
    setShowEvalModal(true);
    setWorkflowVarsMode('manual');
    setEvalResult(null);
    setLastRunVars(null);
    // Extract workflow vars from code
    const code = selectedNode?.data?.data?.code_block || '';
    setWorkflowVars(extractWorkflowVars(code));
  };

  // Handle mode change
  const handleWorkflowVarsModeChange = async (mode) => {
    setWorkflowVarsMode(mode);
    if (mode === 'lastRun') {
      const resp = await fetchLastRunVars();
      if (resp.length) {
        const result = extractWorkflowVarsForLastRun(resp[0]?.workflow_vars);
        setWorkflowVars(result)
      }
    } else {
      // Reset to extracted
      const code = selectedNode?.data?.data?.code_block || '';
      setWorkflowVars(extractWorkflowVars(code));
    }
  };

  const extractWorkflowVarsForLastRun = (obj) => {
    const result = {};

    for (const key in obj) {
      const value = obj[key];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        result[key] = JSON.stringify(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }


  // Handle workflow var change
  const handleWorkflowVarChange = (key, value) => {
    setWorkflowVars((prev) => ({ ...prev, [key]: value }));
  };

  // Evaluate code block
  const handleEvaluate = async () => {
    setIsEvaluating(true);
    setEvalResult(null);
    try {
      const codeBlock = selectedNode?.data?.data?.code_block || '';
      const payload = {
        code_block: [codeBlock],
        request: {},
        workflow_vars: workflowVars,
        verify_bool: false,
      };
      const res = await evaluateNodeExpression(payload);
      setEvalResult(res.data || res);
    } catch (err) {
      setEvalResult({ error: err.message });
    } finally {
      setIsEvaluating(false);
    }
  };

  return (
    <PanelContainer>
      <PanelTitle>Properties</PanelTitle>
      <div style={{ paddingBottom: '1rem', height: 'calc(100dvh - 228px)', overflowY: 'auto' }}>
        <NodeHeader>
          <NodeTypeIcon
            background={nodeTypeInfo.background}
            color={nodeTypeInfo.color}
          >
            <FontAwesomeIcon icon={nodeTypeInfo.icon} />
          </NodeTypeIcon>
          <NodeInfo>
            {/* <NodeType>{nodeTypeInfo.label}</NodeType> */}
            <NodeName>{nodeTypeInfo.label}</NodeName>
            {/* <NodeName>{selectedNode.data.label}</NodeName> */}
          </NodeInfo>
        </NodeHeader>

        <SectionTitle>Basic Properties</SectionTitle>
    {console.log(selectedNode)}
        <FormGroup>
          <Label htmlFor="node-label">Label</Label>
          <Input
            id="node-label"
            value={selectedNode.data.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="node-description">Description</Label>
          <Textarea
            id="node-description"
            value={selectedNode.data.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
          />
        </FormGroup>

        <SectionTitle>Specific Properties</SectionTitle>

        {renderNodeSpecificProperties()}
      </div>
      {/* <PipelineSection>
        <SectionTitle>Pipeline Properties</SectionTitle>

        <FormGroup>
          <Label htmlFor="pipeline-description">Description</Label>
          <Textarea
            id="pipeline-description"
            value={pipelineDescription}
            onChange={(e) => setPipelineDescription(e.target.value)}
            placeholder="Describe the purpose of this pipeline"
          />
        </FormGroup>
      </PipelineSection> */}
      {/* EVALUATION MODAL */}
      {showEvalModal && (
        <>
          <Modal isOpen={showEvalModal} onClose={() => setShowEvalModal(false)} size="large">

            <div className='evalModal' style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Section 1: Workflow Variables */}
              <div>
                <h3>Workflow Variables</h3>
                <div style={{ marginBottom: 8 }}>
                  <label>
                    <input
                      type="radio"
                      checked={workflowVarsMode === 'manual'}
                      onChange={() => handleWorkflowVarsModeChange('manual')}
                    />{' '}
                    Manual
                  </label>
                  <label style={{ marginLeft: 16 }}>
                    <input
                      type="radio"
                      checked={workflowVarsMode === 'lastRun'}
                      onChange={() => handleWorkflowVarsModeChange('lastRun')}
                    />{' '}
                    Use Last Successful Run
                  </label>
                </div>
                <div>
                  {Object.keys(workflowVars).length === 0 && <div style={{ color: '#888' }}>No workflow variables found.</div>}
                  {Object.keys(workflowVars).map((key) => (
                    <div key={key} style={{ marginBottom: 8 }}>
                      <label style={{ marginRight: 8 }}>{key}:</label>
                      {key !== 'ingestion_request' ? <Textarea
                        style={{ width: '100%', fontFamily: 'monospace' }}
                        type="text"
                        value={workflowVars[key]}
                        onChange={e => handleWorkflowVarChange(key, e.target.value)}
                        disabled={workflowVarsMode === 'lastRun'}
                      /> : <pre style={{
                        background: '#f6f6f6',
                        padding: 12,
                        borderRadius: 4,
                        minHeight: 80,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {workflowVars[key] ? JSON.stringify(JSON.parse(workflowVars[key]), null, 2) : 'No result yet.'}
                      </pre>}
                    </div>
                  ))}
                </div>
              </div>
              {/* Section 2: Code Block */}
              <div>
                <h3>Code Block</h3>
                <PythonCodeEditor
                  value={selectedNode?.data?.data?.code_block || ''}
                  onChange={() => { }}
                  readOnly
                />
                <Button onClick={handleEvaluate} disabled={isEvaluating} style={{ marginTop: 12 }}>
                  {isEvaluating ? 'Evaluating...' : 'Play'}
                </Button>
              </div>
              {/* Section 3: Result */}
              <div>
                <h3>Result</h3>
                <pre style={{
                  background: '#f6f6f6',
                  padding: 12,
                  borderRadius: 4,
                  minHeight: 80,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {evalResult ? JSON.stringify(evalResult, null, 2) : 'No result yet.'}
                </pre>
              </div>
            </div>
          </Modal>
        </>
      )}
    </PanelContainer>
  );
};

export default PropertiesPanel;
